import { ComponentTypes } from "entities/template/lib/const/componentTypes";
import type {
    IAutonumberCounter,
    IAutonumberCounterSaveRequest,
    ITemplateAutonumberComponent,
    ITemplateComponent
} from "entities/template/lib/types/template_service_types";
import ru from "../i18n/ru.json";

// Keep the legacy fallback for persisted counters separate from new-counter defaults.
export const DEFAULT_AUTONUMBER_MASK = "{{number}}";
export const INITIAL_AUTONUMBER_MASK = "{{number}}-{{code}}/{{year}}";
// The code is persisted data: its initial value must not depend on the UI language.
export const DEFAULT_AUTONUMBER_CODE = ru.autonumber.defaultCode;
export const AUTONUMBER_TOKENS = [
    "{{number}}",
    "{{number:NN}}",
    "{{day}}",
    "{{month}}",
    "{{year}}",
    "{{code}}"
] as const;

export type AutonumberMaskError = "required" | "number-required" | "invalid-token" | "invalid-number-width" | "code-required";

export interface IAutonumberMaskValidationResult {
    valid: boolean;
    error?: AutonumberMaskError;
}

export interface IAutonumberPreviewOptions {
    number?: number;
    code?: string;
    date?: Date | string;
}

export const isAutonumberCounterNameTaken = (
    name: string,
    counters: IAutonumberCounter[],
    currentCounterId?: number
): boolean => {
    const normalizedName = name.trim();
    return Boolean(normalizedName) && counters.some(counter =>
        counter.id !== currentCounterId && counter.name === normalizedName
    );
};

export const isAutonumberDateSourceMissing = (
    component: ITemplateAutonumberComponent,
    components: ITemplateComponent[]
): boolean => component.counterLoadError === "date-not-found" || (
    component.dateComponentId !== undefined && !components.some(item =>
        item.type === ComponentTypes.dateInput && item.id === component.dateComponentId
    )
);

export const isAutonumberTagDuplicated = (
    component: ITemplateAutonumberComponent,
    components: ITemplateComponent[]
): boolean => {
    const tag = component.name.trim();
    if (!tag) {
        return false;
    }

    return components.some(item =>
        item.type === ComponentTypes.autonumber &&
        item.id !== component.id &&
        item.name?.trim() === tag
    );
};

const resolveDate = (value?: Date | string): Date => {
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
        return value;
    }

    if (typeof value === "string") {
        const displayDateMatch = value.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
        if (displayDateMatch) {
            return new Date(
                Number(displayDateMatch[3]),
                Number(displayDateMatch[2]) - 1,
                Number(displayDateMatch[1])
            );
        }

        const parsedDate = new Date(value);
        if (!Number.isNaN(parsedDate.getTime())) {
            return parsedDate;
        }
    }

    return new Date();
};

const padDatePart = (value: number): string => value.toString().padStart(2, "0");

export const validateAutonumberMaskSyntax = (mask: string): IAutonumberMaskValidationResult => {
    if (!mask || !mask.trim()) {
        return { valid: false, error: "required" };
    }

    const matches = Array.from(mask.matchAll(/\{\{([^{}]+)}}/g));
    const maskWithoutTokens = mask.replace(/\{\{[^{}]+}}/g, "");
    if (/[{}]/.test(maskWithoutTokens)) {
        return { valid: false, error: "invalid-token" };
    }

    let hasNumber = false;
    for (const match of matches) {
        const token = match[1];
        if (token === "number") {
            hasNumber = true;
            continue;
        }

        if (token.startsWith("number:")) {
            hasNumber = true;
            if (!/^[1-9]\d*$/.test(token.slice("number:".length))) {
                return { valid: false, error: "invalid-number-width" };
            }
            continue;
        }

        if (!["day", "month", "year", "code"].includes(token)) {
            return { valid: false, error: "invalid-token" };
        }
    }

    if (!hasNumber) {
        return { valid: false, error: "number-required" };
    }

    return { valid: true };
};

export const validateAutonumberCode = (mask: string, code?: string): IAutonumberMaskValidationResult => {
    if (mask.includes("{{code}}") && !code?.trim()) {
        return { valid: false, error: "code-required" };
    }

    return { valid: true };
};

export const validateAutonumberMask = (mask: string, code?: string): IAutonumberMaskValidationResult => {
    const syntaxValidation = validateAutonumberMaskSyntax(mask);
    if (!syntaxValidation.valid) {
        return syntaxValidation;
    }

    return validateAutonumberCode(mask, code);
};

export const renderAutonumberPreview = (
    mask: string,
    { number = 1, code = "", date }: IAutonumberPreviewOptions = {}
): string => {
    const resolvedDate = resolveDate(date);
    return mask
        .replace(/\{\{number(?::([1-9]\d*))?}}/g, (_, width?: string) => {
            const value = number.toString();
            return width ? value.padStart(Number(width), "0") : value;
        })
        .replaceAll("{{day}}", padDatePart(resolvedDate.getDate()))
        .replaceAll("{{month}}", padDatePart(resolvedDate.getMonth() + 1))
        .replaceAll("{{year}}", resolvedDate.getFullYear().toString())
        .replaceAll("{{code}}", code);
};

export const insertAutonumberToken = (
    mask: string,
    token: string,
    selectionStart: number,
    selectionEnd: number = selectionStart
): string => `${mask.slice(0, selectionStart)}${token}${mask.slice(selectionEnd)}`;

export const autonumberCounterToComponent = (
    counter: IAutonumberCounter,
    component: ITemplateAutonumberComponent,
    components: ITemplateComponent[]
): ITemplateAutonumberComponent => {
    const dateComponent = counter.date_tag
        ? components.find(item => item.type === ComponentTypes.dateInput && item.name === counter.date_tag)
        : undefined;

    return {
        ...component,
        autonumberId: counter.id,
        counterName: counter.name,
        name: counter.tag,
        mask: counter.mask || DEFAULT_AUTONUMBER_MASK,
        code: counter.code || undefined,
        dateComponentId: dateComponent?.id,
        dateTag: counter.date_tag || undefined,
        isAttrUsed: counter.is_attr_used,
        initialCounter: { ...counter },
        counterReadOnly: false,
        counterLoadError: counter.date_tag && !dateComponent ? "date-not-found" : undefined
    };
};

export const autonumberComponentToSaveRequest = (
    component: ITemplateAutonumberComponent,
    components: ITemplateComponent[]
): IAutonumberCounterSaveRequest => {
    const dateComponent = component.dateComponentId === undefined
        ? undefined
        : components.find(item => item.type === ComponentTypes.dateInput && item.id === component.dateComponentId);

    return {
        id: component.autonumberId,
        name: component.counterName.trim(),
        tag: component.name.trim(),
        mask: component.mask || DEFAULT_AUTONUMBER_MASK,
        code: component.code?.trim() || undefined,
        date_tag: dateComponent?.name,
        is_attr_used: component.isAttrUsed
    };
};

const normalizeCounter = (counter: IAutonumberCounterSaveRequest) => ({
    name: counter.name.trim(),
    tag: counter.tag.trim(),
    mask: counter.mask || DEFAULT_AUTONUMBER_MASK,
    code: counter.code?.trim() || "",
    date_tag: counter.date_tag || "",
    is_attr_used: counter.is_attr_used
});

export const isAutonumberCounterChanged = (
    component: ITemplateAutonumberComponent,
    components: ITemplateComponent[]
): boolean => {
    if (component.counterReadOnly) {
        return false;
    }

    if (!component.initialCounter) {
        return true;
    }

    const current = normalizeCounter(autonumberComponentToSaveRequest(component, components));
    const initial = normalizeCounter({
        id: component.initialCounter.id,
        name: component.initialCounter.name,
        tag: component.initialCounter.tag,
        mask: component.initialCounter.mask || DEFAULT_AUTONUMBER_MASK,
        code: component.initialCounter.code || undefined,
        date_tag: component.initialCounter.date_tag || undefined,
        is_attr_used: component.initialCounter.is_attr_used
    });

    return JSON.stringify(current) !== JSON.stringify(initial);
};

